import type {
    CustomAttributes,
    CustomProduct,
    Variant,
} from '@apptus/esales-api';
import type { Product } from 'types';

import type { MarketConfiguration } from '@/config/markets/const';

import getNorceCdnImageUrl from './getNorceCdnImageUrl';
import { getImagesOfProduct } from './mapElevateVariants';

function getAttributesFromProduct(product: CustomProduct) {
    const json = product.typedCustom?.json;
    if (!json?.variantDefiningVariantAttributes) return {};
    return Object.keys(json.variantDefiningVariantAttributes ?? {}).map(
        (key) => ({
            key,
            Label: json.variantDefiningVariantAttributes?.[key].label,
            Values: [json.variantDefiningVariantAttributes?.[key].value],
        }),
    );
}

export function flattenProductCustom(productCustom?: CustomAttributes) {
    if (!productCustom) return;
    const output: Partial<Record<string, Array<string>>> = {};

    for (const key in productCustom) {
        output[key] = productCustom[key].map((attr) => attr.id);
    }

    return output;
}

export function flattenProduct(product: CustomProduct) {
    const json = product.typedCustom?.json as any;
    const custom = flattenProductCustom(product.custom);
    const lengths = product.typedCustom?.lengths ?? {};
    const numbers = product.typedCustom?.numbers ?? {};

    const obj = { ...json, ...custom, ...numbers };

    const output: any = Object.fromEntries(
        Object.entries(lengths).map(([key, value]) => [key, value.amount]),
    );

    for (const key in obj) {
        const values = obj[key as keyof typeof obj];
        if (typeof values === 'string') {
            output[key] = values;
        } else if (typeof values === 'number') {
            output[key] = values;
        } else if (typeof values === 'boolean') {
            output[key] = values;
        } else if (Array.isArray(values)) {
            const arrValue = [];
            for (const value of values) {
                if (typeof value === 'string') {
                    arrValue.push(value);
                } else if ('value' in value) {
                    arrValue.push(value.value);
                } else {
                    arrValue.push(value);
                }
            }
            output[key] = arrValue.length > 1 ? arrValue : arrValue[0];
        } else if (typeof values === 'object' && values !== null) {
            for (const attribute in values) {
                if (typeof values[attribute] === 'string') {
                    output[attribute] = values[attribute];
                } else if (Array.isArray(values[attribute])) {
                    output[attribute] = values[attribute];
                } else if (values[attribute].value) {
                    output[attribute] = values[attribute].value;
                } else if (values[attribute].values) {
                    const arrValue = [];
                    for (const value in values[attribute].values) {
                        if (typeof value === 'string') arrValue.push(value);
                        if (typeof value === 'object' && value !== null) {
                            arrValue.push((value as any)?.value);
                        }
                    }
                    output[attribute] =
                        arrValue.length > 1 ? arrValue : arrValue[0];
                }
            }
        }
    }
    return output;
}

export function getPriceType(variant: Variant) {
    if (variant.prices.some((price) => price.id.endsWith('_campaign'))) {
        return 'campaign';
    }
    if (variant.prices.some((price) => price.id.endsWith('_pricematch'))) {
        return 'priceMatch';
    }
    return 'standard';
}

export function getPriceInfo(product: any, marketConfig: MarketConfiguration) {
    const rrpKey = `rrp_NG_${marketConfig.code}`;
    const vatKey = `vat_NG_${marketConfig.code}`;
    return {
        rrp: product[rrpKey],
        vat: product[vatKey],
    };
}

export function elevateProductToNgProduct(
    product: CustomProduct,
    config: Partial<Product>,
    marketConfig: MarketConfiguration,
    fromPrice: boolean = false,
): Product {
    const {
        variants: [variant],
    } = product;
    const flatProduct = flattenProduct(product);
    const priceType = getPriceType(variant);
    const { vat, rrp } = getPriceInfo(flatProduct, marketConfig);

    const images = getImagesOfProduct(product, marketConfig);

    const output: Product = {
        ...flatProduct,
        id: variant.key,
        slug: variant.link,
        ProductURL: variant.link,
        title: product.title,
        brand: product.brand,
        designer: flatProduct.designerName,
        designers: [flatProduct.designerName].flat(),
        groupId: product.key,
        description: product.description ?? '',
        category: flatProduct.totalCategoryCodes?.[0],
        hasStock: flatProduct.stockOnHand >= 1,
        flags: flatProduct.variantFlags,
        // hasCampaign: false,
        // relatedProducts: '',
        // relatedLightsources: '',
        // recommendedLightsources: '',
        nextDeliveryDate: flatProduct.nextDeliveryDate,
        // deliveryTimeDays: '',
        // lightsourceQuantity: '',
        fromPrice: fromPrice,
        price: product.sellingPrice.min,
        campaignPrice: product.sellingPrice.min < product.listPrice.min,
        originalPrice: product.listPrice.min,
        presentationPrice: product.sellingPrice.min,
        priceType,
        recommendedPrice: rrp,
        presentationOriginal: product.listPrice.min,
        presentationRrpWhenCampaign: rrp,
        vatRate: vat,
        articleStatus: flatProduct.itemStatus,
        stockStatus: flatProduct.stockStatus,
        stock: flatProduct.stockOnHand,
        images,
        variantAttributes: getAttributesFromProduct(product),
        VariantCode: [variant.key],
        'Brands.Code': flatProduct.brandCode,
        'Designers.Code': flatProduct.designerCode,
        ...config,
    };
    return output;
}

export default function mapCustomProducts(
    products: CustomProduct[],
    marketConfig: MarketConfiguration,
) {
    const prices = products.map((p) => p.sellingPrice.min);
    const hasVariants = products.length > 1;
    const fromPrice = Math.min(...prices) < Math.max(...prices);
    return products.map((p) =>
        elevateProductToNgProduct(p, { hasVariants, fromPrice }, marketConfig),
    );
}
